import { css } from 'aphrodite';
import * as React from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { AIMarkdownResponse } from '../../../../../../web/components-v2/ai/AiMarkdownResponse';
import { Button } from '../../../../../../web/components/Button';
import { LoadingSpinner } from '../../../../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../../../../web/components/svgs/icons/SparklesIcon';
import { XIcon } from '../../../../../../web/components/svgs/icons/XIcon';
import { brandPrimary, charmGray } from '../../../../../../web/styles/colors';
import { AidaDraggable } from '../../../../../components/AidaDraggable';
import { DraggableIcon } from '../../../../../components/svgs/icons/audio/DraggableIcon';
import { AnalysisType } from '../../types';
import { styleSheet } from './styles';

interface CallAnalysisModalProps {
	isOpen: boolean;
	onClose: () => void;
	analysisType: AnalysisType | null;
	aiResponse: string;
	isLoading: boolean;
	conversationMessages: { role: 'user' | 'assistant'; content: string }[];
	conversationId?: string;
	onSendFollowUp: (question: string) => void;
	lastAiResponseCompleted: boolean;
	isMultiCall?: boolean;
	selectedCallCount?: number;
}

export const CallAnalysisModal: React.FC<CallAnalysisModalProps> = ({
	isOpen,
	onClose,
	analysisType,
	aiResponse,
	isLoading,
	conversationMessages,
	conversationId,
	onSendFollowUp,
	lastAiResponseCompleted,
	isMultiCall = false,
	selectedCallCount = 0,
}) => {
	const [followUpQuestion, setFollowUpQuestion] = React.useState('');
	const [modalDragging, setModalDragging] = React.useState<{
		active?: boolean;
		x?: number;
		y?: number;
	}>({ active: false, x: 0, y: 0 });

	// Load modal position from localStorage on component mount
	React.useEffect(() => {
		try {
			const savedPosition = localStorage.getItem('callAnalysisModalPosition');
			if (savedPosition) {
				const { x, y } = JSON.parse(savedPosition);
				setModalDragging(prev => ({ ...prev, x, y }));
			}
		} catch (error) {
			console.error('Error loading call analysis modal position from localStorage:', error);
		}
	}, []);

	const onModalDrag = (event: DraggableEvent, { x, y }: DraggableData) => {
		event.stopPropagation();
		setModalDragging({ active: true, x, y });
	};

	const onModalDragStop = (_: DraggableEvent, { x, y }: DraggableData) => {
		const { active } = modalDragging;
		if (active) {
			setModalDragging({ active: false, x, y });

			// Save position to localStorage when drag stops
			try {
				localStorage.setItem('callAnalysisModalPosition', JSON.stringify({ x, y }));
			} catch (error) {
				console.error('Error saving call analysis modal position to localStorage:', error);
			}
		}
	};

	const handleSendFollowUp = React.useCallback(() => {
		if (!followUpQuestion.trim() || !conversationId) return;

		onSendFollowUp(followUpQuestion);
		setFollowUpQuestion('');
	}, [followUpQuestion, conversationId, onSendFollowUp]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendFollowUp();
		}
	};

	if (!isOpen || !analysisType) return null;

	return (
		<AidaDraggable
			onDrag={onModalDrag}
			onStop={onModalDragStop}
			defaultPosition={{ x: modalDragging.x, y: modalDragging.y }}
			position={{ x: modalDragging.x, y: modalDragging.y }}
			handle='.call-analysis-drag-handle'
			isDraggable={true}
		>
			<div className={css(styleSheet.modalContainer)}>
				<div className={css(styleSheet.modalHeader)}>
					<div className={css(styleSheet.modalHeaderContent)}>
						<SparklesIcon className={css(styleSheet.modalIcon)} />
						<h3 className={css(styleSheet.modalTitle)}>
							{isMultiCall ? `${analysisType.label} - Multiple Calls (${selectedCallCount})` : analysisType.label}
						</h3>
					</div>
					<div className={css(styleSheet.modalHeaderActions)}>
						<div className='call-analysis-drag-handle'>
							<DraggableIcon fill={brandPrimary} />
						</div>
						<button onClick={onClose} className={css(styleSheet.iconButton)}>
							<XIcon color={charmGray} height={12} width={12} />
						</button>
					</div>
				</div>
				<div className={css(styleSheet.modalContent)}>
					{!conversationId ? (
						<div className={css(styleSheet.loadingContainerWithColor)}>
							<LoadingSpinner type='small' />
							<div className={css(styleSheet.loadingText)}>Creating AI Conversation...</div>
						</div>
					) : (
						<>
							<div className={css(styleSheet.conversationContainer)}>
								{conversationMessages.map((message, index) => (
									<div
										key={index}
										className={css(
											styleSheet.messageContainer,
											message.role === 'user' ? styleSheet.userMessage : styleSheet.assistantMessage
										)}
									>
										<div className={css(styleSheet.messageRole)}>
											{message.role === 'user' ? 'You' : 'AI Assistant'}
										</div>
										<div className={css(styleSheet.messageContent)}>
											<AIMarkdownResponse>{message.content}</AIMarkdownResponse>
										</div>
									</div>
								))}

								{/* Show streaming AI response */}
								{aiResponse && (!lastAiResponseCompleted || conversationMessages.length === 0) && (
									<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
										<div className={css(styleSheet.messageRole)}>
											AI Assistant
											{isLoading && (
												<span className={css(styleSheet.streamingIndicator)}>
													<LoadingSpinner type='small' />
													Analyzing...
												</span>
											)}
										</div>
										<div className={css(styleSheet.messageContent)}>
											<AIMarkdownResponse>{aiResponse}</AIMarkdownResponse>
										</div>
									</div>
								)}

								{/* Show loading when waiting for response */}
								{isLoading && !aiResponse && (
									<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
										<div className={css(styleSheet.messageRole)}>AI Assistant</div>
										<div className={css(styleSheet.loadingContainer)}>
											<LoadingSpinner type='small' />
											<div className={css(styleSheet.loadingText)}>
												{conversationMessages.length === 0 ? 'Analyzing your call...' : 'Thinking...'}
											</div>
										</div>
									</div>
								)}
							</div>

							{/* Follow-up question input - only show when not loading */}
							{!isLoading && (
								<div className={css(styleSheet.inputContainer)}>
									<textarea
										className={css(styleSheet.textarea)}
										value={followUpQuestion}
										onChange={e => setFollowUpQuestion(e.target.value)}
										onKeyPress={handleKeyPress}
										placeholder={`Ask a follow-up question about the ${analysisType.label.toLowerCase()}...`}
										disabled={isLoading}
										rows={3}
									/>
									<div className={css(styleSheet.buttonContainer)}>
										<Button
											kind='secondary'
											size='small'
											onClick={handleSendFollowUp}
											disabled={isLoading || !followUpQuestion.trim()}
										>
											Send Question
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</AidaDraggable>
	);
};
